/**
 * Iter 34 — Admin Coverage & Risk (read-only).
 *
 * Aggregates open health-check violations across every provider's
 * active period into a single, sortable list. Same engine the
 * provider sees on their own Health tab — admin and provider always
 * agree on truth.
 */
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Warning, ArrowRight, ListChecks, UserCircleMinus } from "@phosphor-icons/react";
import { useAdminCoverage } from "@/lib/adminQueries";
import { RefreshChip } from "@/lib/formatUpdatedAgo";
import AdminShell from "./AdminShell";
import { MessageProviderButton } from "@/components/messaging/MessageProviderButton";

const KIND_LABELS = {
  service_obligation_no_blocks: "No scheduled blocks",
  service_obligation_under_minutes: "Under-scheduled minutes",
  service_obligation_over_minutes: "Over-scheduled minutes",
  grade_constraint: "Grade-rule overlap",
  provider_unavailable_day: "Provider day-off conflict",
  provider_blocked_time: "Provider blocked-time conflict",
  provider_partial_window: "Outside provider window",
  flex_day_outside_window: "Outside flex window",
  flex_day_closed: "Flex day closed",
  attendance_window: "Outside attendance window",
  cross_provider: "Cross-provider overlap",
};

const KIND_ACCENT = {
  service_obligation_no_blocks: "red",
  service_obligation_under_minutes: "amber",
  service_obligation_over_minutes: "amber",
  grade_constraint: "amber",
  provider_unavailable_day: "red",
  provider_blocked_time: "red",
  cross_provider: "red",
};

function GroupCard({ kind, items, onProviderClick }) {
  const accent = KIND_ACCENT[kind] || "default";
  const accentClass = {
    red: "border-[#B85C5C]/25 bg-[#FBEFEC]/30",
    amber: "border-[#9C6B2E]/25 bg-[#FDF8F3]/40",
    default: "border-[#E5E3DB] bg-white",
  }[accent];
  const dotClass = {
    red: "bg-[#B85C5C]",
    amber: "bg-[#9C6B2E]",
    default: "bg-[#9B9890]",
  }[accent];
  return (
    <Card className={`border ${accentClass}`} data-testid={`risk-group-${kind}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#1F2937] heading-font flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${dotClass}`} />
          {KIND_LABELS[kind] || kind}
          <Badge variant="outline" className="ml-1 text-[10px] border-[#E5E3DB]">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1.5">
          {items.slice(0, 6).map((v, i) => (
            <div
              key={`${v.provider?.id}-${i}`}
              data-testid={`risk-row-${v.provider?.id}-${i}`}
              className="flex items-center justify-between gap-2 bg-white border border-[#E5E3DB] rounded-md px-2.5 py-1.5"
            >
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-[#1F2937] truncate">{v.provider?.name}</p>
                <p className="text-[10px] text-[#6B7280] truncate">
                  {v.student_name && <>{v.student_name} · </>}
                  {v.message || v.detail || ""}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <MessageProviderButton
                  testid={`risk-row-message-${v.provider?.id}-${i}`}
                  size="xs"
                  variant="ghost"
                  label=""
                  provider={v.provider}
                  context={{
                    kind: "coverage_violation",
                    violation_id: v.id,
                    violation_label: KIND_LABELS[kind] || kind,
                    student_id: v.student_id,
                    student_name: v.student_name,
                    student_readable_id: v.student_readable_id,
                    ref_url: "/schedule?tab=health",
                    ref_label: v.message || v.detail || "",
                  }}
                />
                <button
                  onClick={() => onProviderClick?.(v.provider?.id)}
                  className="text-[10px] text-[#3F4756] hover:underline inline-flex items-center gap-0.5"
                >
                  View calendar <ArrowRight size={10} />
                </button>
              </div>
            </div>
          ))}
          {items.length > 6 && (
            <p className="text-[10px] text-[#9B9890] italic mt-1">+{items.length - 6} more in this category</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminCoverage() {
  const navigate = useNavigate();
  // FP3-053 — Super-admin context propagation. The sidebar selector
  // sets these via the URL; we forward them so the coverage feed
  // narrows server-side to the chosen org/school.
  const [searchParams] = useSearchParams();
  const asOrg = (searchParams.get("as_org") || "").trim();
  const asSchool = (searchParams.get("as_school") || "").trim();

  // FP3-119B — Steady-state cache via React Query. Data renders
  // immediately from cache on revisits; only refetches when stale
  // (3 min) or when the manual Refresh chip is used. Cache key
  // includes user/org/shadow scope so an org admin never sees the
  // super_admin's cached rows and vice versa.
  const coverageQuery = useAdminCoverage({
    ...(asOrg ? { as_org: asOrg } : {}),
    ...(asSchool ? { as_school: asSchool } : {}),
  });
  const data = coverageQuery.data;
  const loading = coverageQuery.isPending;
  const refetching = coverageQuery.isFetching && !coverageQuery.isPending;
  const dataUpdatedAt = coverageQuery.dataUpdatedAt;

  const groups = useMemo(() => {
    if (!data) return [];
    const by = {};
    for (const v of data.violations || []) {
      const k = v.kind || "unknown";
      (by[k] ||= []).push(v);
    }
    // Sort kinds by count desc
    return Object.entries(by).sort((a, b) => b[1].length - a[1].length);
  }, [data]);

  return (
    <AdminShell
      title="Coverage & Risk"
      subtitle="Open issues across every provider's active period · same engine providers see on their own Health tab"
    >
      <div className="mb-3 flex items-center justify-end">
        <RefreshChip
          dataUpdatedAt={dataUpdatedAt}
          isFetching={refetching}
          onRefresh={() => coverageQuery.refetch()}
          testid="coverage-refresh"
        />
      </div>
      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : !data ? (
        <p className="text-[13px] text-[#6B7280]">No data.</p>
      ) : (
        <div className="space-y-4">
          {/* Summary strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-[#3F4756]/15 bg-[#3F4756]/5 p-4" data-testid="risk-summary-total">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Total open issues</p>
                <ListChecks size={16} weight="duotone" className="text-[#3F4756]" />
              </div>
              <p className="text-3xl font-bold text-[#1F2937] mt-2 tabular-nums">{data.summary.total_violations}</p>
              <p className="text-[11px] text-[#6B7280] mt-1">Across {data.summary.providers_with_issues} provider{data.summary.providers_with_issues === 1 ? "" : "s"}</p>
            </div>
            <div className="rounded-xl border border-[#9C6B2E]/20 bg-[#FDF8F3] p-4" data-testid="risk-summary-cancelled">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-[#9C6B2E] font-semibold">Sessions cancelled this week</p>
                <UserCircleMinus size={16} weight="duotone" className="text-[#9C6B2E]" />
              </div>
              <p className="text-3xl font-bold text-[#1F2937] mt-2 tabular-nums">{data.summary.cancelled_this_week_unavail}</p>
              <p className="text-[11px] text-[#6B7280] mt-1">From provider unavailability</p>
            </div>
            <div className="rounded-xl border border-[#E5E3DB] bg-white p-4" data-testid="risk-summary-categories">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Issue categories</p>
                <Warning size={16} weight="duotone" className="text-[#6B7280]" />
              </div>
              <p className="text-3xl font-bold text-[#1F2937] mt-2 tabular-nums">{Object.keys(data.summary.by_kind || {}).length}</p>
              <p className="text-[11px] text-[#6B7280] mt-1">Distinct violation kinds</p>
            </div>
          </div>

          {/* Iter 41 — Minutes-compliance strip. Surfaces each of the
              three service-obligation states (no blocks, under, over)
              as a standing tile so admins always see coverage vs
              over-service at a glance, regardless of which states
              currently have rows. Clicking a tile scrolls to the
              matching group card below. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="minutes-compliance-strip">
            {[
              {
                kind: "service_obligation_no_blocks",
                label: "No scheduled blocks",
                sub: "Commitments without any recurring block",
                tone: "red",
              },
              {
                kind: "service_obligation_under_minutes",
                label: "Under-scheduled minutes",
                sub: "Scheduled below commitment (&lt; 95%)",
                tone: "amber",
              },
              {
                kind: "service_obligation_over_minutes",
                label: "Over-scheduled minutes",
                sub: "Scheduled above commitment (&gt; 105%)",
                tone: "amber",
              },
            ].map((t) => {
              const count = data.summary.by_kind?.[t.kind] || 0;
              const toneClass = t.tone === "red"
                ? "border-[#B85C5C]/25 bg-[#FBEFEC]/40"
                : "border-[#9C6B2E]/20 bg-[#FDF8F3]/60";
              const labelClass = t.tone === "red" ? "text-[#B85C5C]" : "text-[#9C6B2E]";
              return (
                <button
                  key={t.kind}
                  data-testid={`minutes-tile-${t.kind}`}
                  onClick={() => {
                    if (count === 0) return;
                    const el = document.querySelector(`[data-testid="risk-group-${t.kind}"]`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  disabled={count === 0}
                  className={`rounded-xl border text-left p-4 transition-colors ${toneClass} ${
                    count === 0 ? "opacity-60 cursor-default" : "hover:shadow-sm hover:border-[#3F4756]/30"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className={`text-[10px] uppercase tracking-wider font-semibold ${labelClass}`}>{t.label}</p>
                    <Warning size={14} weight="duotone" className={labelClass} />
                  </div>
                  <p className="text-3xl font-bold text-[#1F2937] mt-2 tabular-nums">{count}</p>
                  <p
                    className="text-[11px] text-[#6B7280] mt-1"
                    dangerouslySetInnerHTML={{ __html: t.sub }}
                  />
                </button>
              );
            })}
          </div>

          {/* Grouped violations */}
          {groups.length === 0 ? (
            <Card className="border-[#1E5A3F]/30 bg-[#E1EAE4]/30">
              <CardContent className="py-12 text-center">
                <p className="text-sm font-medium text-[#1E5A3F]">All clear</p>
                <p className="text-[11px] text-[#6B7280] mt-1">No open issues across any provider's active period.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {groups.map(([kind, items]) => (
                <GroupCard
                  key={kind}
                  kind={kind}
                  items={items}
                  onProviderClick={(pid) => navigate(`/admin/calendar?provider=${pid}`)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </AdminShell>
  );
}
